export class SfuControlClient {
  constructor(urlResolver, tokenProvider) {
    this.urlResolver = urlResolver // function returning ws url for /ctrl
    this.tokenProvider = tokenProvider // async () => token
    this.ws = null
    this._peerId = null
    this._connectPromise = null
    this._connectingPeerId = null
    this._handlers = new Map()
  }

  on(type, cb) {
    const list = this._handlers.get(type) || []
    list.push(cb)
    this._handlers.set(type, list)
  }

  _emit(type, data) {
    const list = this._handlers.get(type) || []
    list.forEach((cb) => {
      try {
        cb(data)
      } catch {}
    })
  }

  send(action, data) {
    try {
      const ws = this.ws
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        console.warn('[CTRL] drop message; ws not open', {
          action,
          state: ws ? ws.readyState : 'none'
        })
        return
      }
      ws.send(JSON.stringify({ action, data }))
    } catch (e) {
      console.warn('[CTRL] send failed', action, e?.message || e)
    }
  }

  isOpen() {
    return !!(this.ws && this.ws.readyState === WebSocket.OPEN)
  }

  async ensureConnected(peerId) {
    if (this.isOpen()) return true
    const id = String(peerId || this._peerId || '')
    if (!id) return false
    try {
      await this.connect(id)
      return true
    } catch {
      return false
    }
  }

  async connect(peerId) {
    const targetPeerId = String(peerId || '').trim()
    if (!targetPeerId) throw new Error('peerId required')

    // Already connected for the same peer.
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this._peerId === targetPeerId) {
      return
    }

    // Reuse in-flight connect for the same peer to avoid CONNECTING close races.
    if (this._connectPromise && this._connectingPeerId === targetPeerId) {
      return this._connectPromise
    }

    // Wait for any previous connect attempt to settle before starting a new one.
    if (this._connectPromise) {
      try {
        await this._connectPromise
      } catch {
        /* ignore */
      }
      if (this.ws && this.ws.readyState === WebSocket.OPEN && this._peerId === targetPeerId) {
        return
      }
    }

    // Cleanup existing connection if any (different peer or stale socket).
    if (this.ws) {
      try {
        this.ws.close()
      } catch {}
      this.ws = null
    }

    const url = typeof this.urlResolver === 'function' ? this.urlResolver() : ''
    const ctrlUrl = toCtrlWsUrl(url)
    const token = await (this.tokenProvider ? this.tokenProvider() : null)
    this._connectingPeerId = targetPeerId
    this._connectPromise = new Promise((resolve, reject) => {
      let settled = false
      const done = (ok, err) => {
        if (settled) return
        settled = true
        if (ok) resolve()
        else reject(err || 'ctrl-ws-error')
      }
      try {
        const ws = new WebSocket(ctrlUrl)
        this.ws = ws
        this._peerId = targetPeerId
        ws.onopen = () => {
          // Send raw without using this.send() to avoid "ws not open" check race condition
          try {
            ws.send(JSON.stringify({ action: 'register', data: { peerId: targetPeerId, token } }))
          } catch (e) {
            console.warn('[CTRL] register failed', e)
            done(false, e?.message || e)
          }
        }
        ws.onmessage = (ev) => {
          const msg = JSON.parse(ev.data || '{}')
          if (msg.action === 'registered') done(true)
          else this._emit(msg.action, msg.data)
        }
        ws.onerror = (e) => done(false, e?.message || 'ctrl-ws-error')
        ws.onclose = () => {
          try {
            this.ws = null
          } catch {}
          this._emit('closed')
          // If close happens before register ack, treat as failed connect.
          if (!settled) done(false, 'ctrl-ws-closed')
        }
      } catch (e) {
        done(false, e)
      }
    })
    try {
      await this._connectPromise
    } finally {
      this._connectPromise = null
      this._connectingPeerId = null
    }
  }

  ring({ toPeerId, conversationId, mode = 'direct', fromPeerId, fromDisplayName, fromPhotoURL }) {
    this.send('ring', {
      toPeerId,
      conversationId,
      mode,
      fromPeerId,
      fromDisplayName,
      fromPhotoURL
    })
  }
  accept({ toPeerId, conversationId }) {
    this.send('accept', { toPeerId, conversationId })
  }
  decline({ toPeerId, conversationId }) {
    this.send('decline', { toPeerId, conversationId })
  }
  hangup({ toPeerId, conversationId }) {
    this.send('hangup', { toPeerId, conversationId })
  }
}

function toCtrlWsUrl(rawUrl) {
  const input = String(rawUrl || '').trim()
  if (!input) return 'ws://localhost:3001/ctrl'
  try {
    const u = new URL(input)
    const path = String(u.pathname || '').replace(/\/+$/, '')
    if (path.endsWith('/ws')) {
      u.pathname = path.replace(/\/ws$/, '/ctrl')
    } else if (!path) {
      u.pathname = '/ctrl'
    } else if (!path.endsWith('/ctrl')) {
      u.pathname = `${path}/ctrl`
    }
    return u.toString()
  } catch {
    const cleaned = input.replace(/\/+$/, '')
    if (/\/ws(\?|$)/.test(cleaned)) return cleaned.replace(/\/ws(\?|$)/, '/ctrl$1')
    if (/\/ctrl(\?|$)/.test(cleaned)) return cleaned
    return `${cleaned}/ctrl`
  }
}

export default { SfuControlClient }
